import React, { useMemo } from 'react'
import { Container, Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { BlogArticleListAllQueryResult } from '@graphql/queries/__generated__/blog.generated'
import makeStyles from '@mui/styles/makeStyles'
import ArticleCard from '@components/Blog/ArticleCard'
import { BlogArticleFragment } from '@graphql/fragments/__generated__/BlogArticle.generated'
import Typography from '@mui/material/Typography'
import { getWholesaleBlogArticleUrl } from '../../../urls'

const useStyles = makeStyles((theme: Theme) => ({
  articles: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gridGap: 20,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr 1fr 1fr',
      gridGap: 15,
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr 1fr',
      gridGap: 15,
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
}))

interface Props {
  articlesQueryResult: BlogArticleListAllQueryResult
  blogSlug: string

  fetchMore(): Promise<void>
}

const Page: React.FC<Props> = ({ articlesQueryResult, blogSlug, fetchMore }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const articles = useMemo<BlogArticleFragment[]>(() => {
    if (articlesQueryResult.loading && !articlesQueryResult.data) {
      return articlesQueryResult.previousData?.blogArticleList.edges.map(edge => edge.node) || []
    }
    return articlesQueryResult.data?.blogArticleList.edges.map(edge => edge.node) || []
  }, [articlesQueryResult.data, articlesQueryResult.previousData, articlesQueryResult.loading])

  return (
    <div>
      <div className='md:py-20'>
        <Container maxWidth='lg'>
          <Typography variant='h4' paragraph>
            {t('title.exploreCountArticles', {
              defaultValue: 'Explore {{count}} articles',
              count: articlesQueryResult.data?.blogArticleList.totalCount || 0,
            })}
          </Typography>
          <div className={classes.articles}>
            {articles.map(node => (
              <div key={node.id}>
                <ArticleCard
                  article={node}
                  blogSlug={blogSlug}
                  url={getWholesaleBlogArticleUrl(blogSlug, node.slug)}
                  updateMeasure={() => null}
                />
              </div>
            ))}
          </div>
          {articlesQueryResult.data?.blogArticleList.pageInfo.hasNextPage && (
            <div className='pb-10 pt-20 flex justify-center px-20'>
              <button
                className='btn btn-secondary max-w-full w-500'
                onClick={fetchMore}
                disabled={articlesQueryResult.loading}
              >
                {t('button.loadMore', 'Load more')}
              </button>
            </div>
          )}
        </Container>
      </div>
    </div>
  )
}

export default Page
